/**
 * Cell value formatters for the QC Task MRT (MT-QC-TASK-001).
 * Pure functions — safe to call from column defs and easy to unit test.
 */

/** Display string for empty / missing table cells (env override). */
export const QC_TABLE_EMPTY_LABEL =
  import.meta.env.VITE_TABLE_EMPTY_VALUE_LABEL || 'NA';

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatQcTaskCellValue(value) {
  if (value === null || value === undefined) return QC_TABLE_EMPTY_LABEL;
  if (typeof value === 'string' && value.trim() === '')
    return QC_TABLE_EMPTY_LABEL;
  return value;
}

/**
 * Primary cell label for assignment user objects (`assigned_to`, `assigned_reviewer`).
 * API shape: `{ auth_user_id, name, email, phone }` (see assignmentCtrl json_build_object).
 *
 * @param {unknown} value
 * @returns {string}
 */
export function formatQcTaskUserDisplayName(value) {
  if (value === null || value === undefined) return QC_TABLE_EMPTY_LABEL;
  if (typeof value === 'string') {
    const t = value.trim();
    return t === '' ? QC_TABLE_EMPTY_LABEL : t;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const name = value.name != null ? String(value.name).trim() : '';
    if (name) return name;
    const email = value.email != null ? String(value.email).trim() : '';
    if (email) return email;
    const phoneRaw = value.phone ?? value.phone_number;
    const phone = phoneRaw != null ? String(phoneRaw).trim() : '';
    if (phone) return phone;
    const id =
      value.auth_user_id != null ? String(value.auth_user_id).trim() : '';
    if (id) return id;
    return QC_TABLE_EMPTY_LABEL;
  }
  return String(formatQcTaskCellValue(value));
}

/**
 * True when the API returned a user object with at least one non-empty field to show in details.
 * @param {unknown} value
 * @returns {boolean}
 */
export function qcTaskUserRowHasDetailFields(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return ['auth_user_id', 'name', 'email', 'phone', 'phone_number'].some(
    (k) => {
      const v = value[k];
      return v !== null && v !== undefined && String(v).trim() !== '';
    },
  );
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatQcTaskDuration(value) {
  if (value === null || value === undefined || value === '')
    return QC_TABLE_EMPTY_LABEL;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return QC_TABLE_EMPTY_LABEL;
  return numeric.toFixed(2);
}

/**
 * @param {unknown} value - ISO date string or timestamp.
 * @returns {string}
 */
export function formatQcTaskDateTime(value) {
  if (value === null || value === undefined || value === '')
    return QC_TABLE_EMPTY_LABEL;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return QC_TABLE_EMPTY_LABEL;
  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}
